import { of, switchMap, tap, merge, fromEvent, ReplaySubject, first, share, catchError, EMPTY, combineLatest, map } from 'rxjs';

import { replaceViewContainer, renderTemplate } from './ui.js';
import { getCollectionMetadata, createCollectionInvite, listCollectionInvites, deleteCollectionInvite, listCollectionMembers, removeCollectionMember } from './knowledge-api.js';
import { userInfo$ } from './http.js';
import { buildWorkMonitor } from './working.js';

function formatDate(dateStr) {
    let d = new Date(dateStr);
    let yyyy = d.getFullYear();
    let mm = String(d.getMonth() + 1).padStart(2, '0');
    let dd = String(d.getDate()).padStart(2, '0');
    let hh = String(d.getHours()).padStart(2, '0');
    let min = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

export function createCollectionSharingView(colId) {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('collection-sharing-view');
            replaceViewContainer(root);

            let saveMonitor = buildWorkMonitor();
            let saveIndicator$ = saveMonitor.showElements('.saving');
            let alertSavingBeforeUnload$ = saveMonitor.alertBeforeUnload();

            let meta$ = getCollectionMetadata(colId).pipe(
                first(),
                share({
                    connector: () => new ReplaySubject(1),
                    resetOnComplete: true,
                    resetOnError: true,
                    resetOnRefCountZero: true,
                }),
            );

            let metaWithOwnership$ = combineLatest([meta$, userInfo$.pipe(first())]).pipe(
                map(([meta, userInfo]) => ({ ...meta, is_owner: meta.owner === userInfo.oauth.sub })),
                share({
                    connector: () => new ReplaySubject(1),
                    resetOnComplete: true,
                    resetOnError: true,
                    resetOnRefCountZero: true,
                }),
            );

            let renderMeta$ = metaWithOwnership$.pipe(
                tap(meta => {
                    root.querySelector('.collection-title').textContent = `${meta.owner}/${meta.name}`;
                    if (meta.is_owner) {
                        root.querySelector('.owner-section').classList.remove('hidden');
                    } else {
                        root.querySelector('.member-section').classList.remove('hidden');
                    }
                }),
            );

            // --- owner section ---

            let inviteReload$ = new ReplaySubject(1);
            let memberReload$ = new ReplaySubject(1);

            let newInviteEl = root.querySelector('.new-invite');
            let inviteUrlEl = root.querySelector('.invite-url');
            let invitesContainer = root.querySelector('.invites-container');
            let membersContainer = root.querySelector('.members-container');

            let createInvite$ = fromEvent(root.querySelector('.create-invite'), 'click').pipe(
                switchMap(() => saveMonitor.zone(createCollectionInvite(colId))),
                tap(result => {
                    let url = `${window.location.origin}${window.location.pathname}#accept/${result.token}`;
                    inviteUrlEl.textContent = url;
                    newInviteEl.classList.remove('hidden');
                    inviteReload$.next();
                }),
            );

            let renderInvites$ = inviteReload$.pipe(
                switchMap(() => listCollectionInvites(colId)),
                tap(invites => {
                    invitesContainer.innerHTML = '';
                    if (invites.length === 0) {
                        invitesContainer.textContent = 'No pending invites.';
                        return;
                    }
                    for (let invite of invites) {
                        let inviteEl = renderTemplate('invite-widget');
                        inviteEl.setAttribute('data-token', invite.token);
                        inviteEl.querySelector('.invite-created').textContent = `created: ${formatDate(invite.created_at)}`;
                        inviteEl.querySelector('.invite-expires').textContent = `expires: ${formatDate(invite.expires_at)}`;
                        inviteEl.querySelector('.revoke-invite').addEventListener('click', () => {
                            deleteCollectionInvite(colId, invite.token).subscribe({
                                next: () => inviteReload$.next(),
                                error: err => console.error('Failed to revoke invite:', err),
                            });
                        }, false);
                        invitesContainer.appendChild(inviteEl);
                    }
                }),
                catchError(err => {
                    console.warn('Failed to load invites:', err);
                    return EMPTY;
                }),
            );

            let renderMembers$ = memberReload$.pipe(
                switchMap(() => listCollectionMembers(colId)),
                tap(members => {
                    membersContainer.innerHTML = '';
                    if (members.length === 0) {
                        membersContainer.textContent = 'No members yet.';
                        return;
                    }
                    for (let member of members) {
                        let memberEl = renderTemplate('member-widget');
                        memberEl.setAttribute('data-member-sub', member.member_sub);
                        memberEl.querySelector('.member-sub').textContent = member.member_sub;
                        memberEl.querySelector('.member-joined').textContent = `joined: ${formatDate(member.joined_at)}`;
                        memberEl.querySelector('.remove-member').addEventListener('click', () => {
                            removeCollectionMember(colId, member.member_sub).subscribe({
                                next: () => memberReload$.next(),
                                error: err => console.error('Failed to remove member:', err),
                            });
                        }, false);
                        membersContainer.appendChild(memberEl);
                    }
                }),
                catchError(err => {
                    console.warn('Failed to load members:', err);
                    return EMPTY;
                }),
            );

            // --- member section ---

            let leaveCollection$ = fromEvent(root.querySelector('.leave-collection'), 'click').pipe(
                switchMap(() => userInfo$.pipe(first())),
                switchMap(userInfo => saveMonitor.zone(
                    removeCollectionMember(colId, userInfo.sub),
                )),
                tap(() => {
                    window.location.href = '#';
                }),
            );

            // trigger initial loads after meta resolves (only owner has invites/members)
            let initOwnerSection$ = metaWithOwnership$.pipe(
                tap(meta => {
                    if (meta.is_owner) {
                        inviteReload$.next();
                        memberReload$.next();
                    }
                }),
            );

            return merge(
                saveIndicator$,
                alertSavingBeforeUnload$,
                renderMeta$,
                initOwnerSection$,
                createInvite$,
                renderInvites$,
                renderMembers$,
                leaveCollection$,
            );
        }),
    );
}
